import { useQuery } from '@tanstack/react-query';

import { indicatorEndpoints, type InvestmentSetListItem } from '@d3/contract';

import { api } from '@shared/api';

import { sortByPortfolioOrder } from '../lib/portfolio-order';
import type { PortfolioListItem } from '../model/portfolio-list';
import { toPortfolioListItem } from './investment-set.mapper';
import { portfolioKeys } from './portfolio.keys';

/** 모든 포트폴리오 목록이 같은 순서(정렬 순서 값 내림차순 → 회사 번호, sortByPortfolioOrder) — 서버는 회사 · 펀드 번호 순으로 준다 */
const selectList = (data: { items: InvestmentSetListItem[] }): PortfolioListItem[] =>
  sortByPortfolioOrder(data.items.map(toPortfolioListItem));

/**
 * 포트폴리오 목록(회사 × 펀드 한 줄씩) — 지표 포함 조회(`GET /investment-sets`, indicator 도메인).
 * 메인 · 펀드 상세의 "지표가 붙은 목록"용. fund 를 주면 그 펀드의 것만(순서는 메인과 같다).
 * 경량 포트폴리오 목록(Select 등)은 다른 엔드포인트(`GET /portfolios`)로 분리돼 있다 — 섞지 않는다.
 */
export function useInvestmentSetsQuery(params: { fund?: number } = {}) {
  const { fund } = params;
  return useQuery({
    throwOnError: true,
    queryKey: portfolioKeys.list({ kind: 'investment-set', fund }),
    queryFn: ({ signal }) =>
      api.request(indicatorEndpoints.listInvestmentSets, {
        query: fund === undefined ? {} : { fundId: fund },
        signal,
      }),
    select: selectList,
  });
}

/** 내 담당 포트폴리오(D3 · Admin) — 같은 엔드포인트에 `mine=true`. enabled=false 면 부르지 않는다(LP) */
export function useMyInvestmentSetsQuery(options: { enabled: boolean }) {
  return useQuery({
    throwOnError: true,
    queryKey: portfolioKeys.list({ kind: 'investment-set', scope: 'mine' }),
    queryFn: ({ signal }) =>
      api.request(indicatorEndpoints.listInvestmentSets, { query: { mine: true }, signal }),
    select: selectList,
    enabled: options.enabled,
  });
}
